import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  Lock,
  ArrowLeft,
  MessageCircle,
  HandHeart,
  CalendarDays,
  ShieldCheck,
  Share2,
  Copy,
  Check,
  Inbox,
} from "lucide-react";
import { needTypeLabels } from "../mock";
import { useToast } from "../hooks/use-toast";
import { ContentEditor, NeedsManager, FeedManager } from "../components/admin/ManagePanels";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full h-11 rounded-2xl bg-secondary/60 border-0 px-4 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40";
const textareaCls =
  "w-full rounded-2xl bg-secondary/60 border-0 px-4 py-3 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 min-h-[90px] resize-none";
const submitCls =
  "rounded-full bg-primary text-primary-foreground px-5 py-2 text-sm font-medium hover:bg-primary/90 transition-gentle disabled:opacity-60";

const AdminPage = ({ forcedSlug }) => {
  const params = useParams();
  const slug = forcedSlug || params.slug;
  const { toast } = useToast();

  const [data, setData] = useState(null);
  const [adminCode, setAdminCode] = useState("");
  const [error, setError] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const [update, setUpdate] = useState({ author: "", title: "", body: "", fromCareTeam: false });
  const [need, setNeed] = useState({ type: "meal", title: "", details: "", category: "main" });
  const [milestone, setMilestone] = useState({ date: "", title: "", note: "" });
  const [busy, setBusy] = useState("");
  const [copied, setCopied] = useState(false);

  const storedCode = () => sessionStorage.getItem(`hart_admin_${slug}`) || "";

  const verify = async (code, { silent } = {}) => {
    setVerifying(true);
    try {
      const res = await axios.post(`${API}/persons/${slug}/admin/verify`, { adminPasscode: code });
      setData(res.data);
      sessionStorage.setItem(`hart_admin_${slug}`, code);
      setError(false);
    } catch (e) {
      sessionStorage.removeItem(`hart_admin_${slug}`);
      if (!silent) setError(true);
    } finally {
      setVerifying(false);
    }
  };

  useEffect(() => {
    if (storedCode()) verify(storedCode(), { silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (!data) {
    return (
      <main className="min-h-screen bg-gradient-warm flex items-center justify-center px-6">
        <div className="w-full max-w-md animate-fade-up">
          <div className="rounded-3xl bg-card shadow-card p-8 sm:p-10 text-center">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary-soft">
              <ShieldCheck className="h-5 w-5 text-primary" />
            </div>
            <h1 className="mt-5 font-serif text-3xl text-foreground">Family admin</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              For the family only. Enter the admin passcode to post updates, needs, and appointments.
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (adminCode.trim()) verify(adminCode.trim());
              }}
              className="mt-6"
            >
              <input
                type="password"
                value={adminCode}
                data-testid="admin-passcode-input"
                onChange={(e) => {
                  setAdminCode(e.target.value);
                  setError(false);
                }}
                placeholder="Admin passcode"
                className="w-full h-12 rounded-2xl bg-secondary/60 border-0 px-4 text-center text-lg tracking-[0.2em] placeholder:tracking-normal placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              />
              {error && (
                <p className="mt-2 text-xs text-destructive" data-testid="admin-passcode-error">
                  That admin passcode doesn't match. Please try again.
                </p>
              )}
              <button
                type="submit"
                disabled={verifying}
                data-testid="admin-passcode-submit"
                className="mt-4 w-full rounded-full bg-gradient-rose text-primary-foreground px-6 py-3 text-sm font-medium shadow-soft hover:shadow-glow transition-gentle disabled:opacity-60"
              >
                {verifying ? "Checking…" : "Enter"}
              </button>
            </form>
          </div>
          <Link
            to={`/${slug}`}
            className="mt-6 flex items-center justify-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-gentle"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to the page
          </Link>
        </div>
      </main>
    );
  }

  const { person } = data;

  const shareText = `We've set up a private page for ${person.firstName} where our family shares updates, needs, and prayers.\n\nVisit: ${window.location.origin}/${slug}\nPasscode: ${data.visitorPasscode}\n\nYour messages and prayers mean the world to us. Thank you for loving ${person.firstName}.`;

  const copyShareText = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
      toast({ title: "Copied", description: "Paste it into a text, email, or group chat." });
    } catch (e) {
      toast({ title: "Couldn't copy", description: "Please select and copy the text manually." });
    }
  };

  const postUpdate = async (e) => {
    e.preventDefault();
    if (!update.author.trim() || !update.body.trim()) {
      toast({ title: "Almost there", description: "Please fill in your name and the update." });
      return;
    }
    setBusy("update");
    try {
      const res = await axios.post(`${API}/persons/${slug}/updates`, {
        adminPasscode: storedCode(),
        ...update,
      });
      setData((prev) => ({ ...prev, updates: [res.data, ...prev.updates] }));
      setUpdate({ author: "", title: "", body: "", fromCareTeam: false });
      toast({ title: "Update posted", description: "Everyone visiting the page will see it." });
    } catch (err) {
      toast({ title: "Couldn't post", description: "Please try again." });
    } finally {
      setBusy("");
    }
  };

  const postNeed = async (e) => {
    e.preventDefault();
    if (!need.title.trim()) {
      toast({ title: "Almost there", description: "Please give the need a title." });
      return;
    }
    setBusy("need");
    try {
      const res = await axios.post(`${API}/persons/${slug}/needs`, {
        adminPasscode: storedCode(),
        ...need,
      });
      setData((prev) => ({ ...prev, needs: [...prev.needs, res.data] }));
      setNeed({ type: "meal", title: "", details: "", category: "main" });
      toast({ title: "Need added", description: "Visitors can now offer to help." });
    } catch (err) {
      toast({ title: "Couldn't add", description: "Please try again." });
    } finally {
      setBusy("");
    }
  };

  const postMilestone = async (e) => {
    e.preventDefault();
    if (!milestone.title.trim() || !milestone.date) {
      toast({ title: "Almost there", description: "Please add a date and a title." });
      return;
    }
    setBusy("milestone");
    try {
      const res = await axios.post(`${API}/persons/${slug}/milestones`, {
        adminPasscode: storedCode(),
        ...milestone,
      });
      setData((prev) => ({
        ...prev,
        milestones: [...prev.milestones, res.data].sort((a, b) => a.date.localeCompare(b.date)),
      }));
      setMilestone({ date: "", title: "", note: "" });
      toast({ title: "Appointment added", description: "It now shows in Milestones ahead." });
    } catch (err) {
      toast({ title: "Couldn't add", description: "Please try again." });
    } finally {
      setBusy("");
    }
  };

  return (
    <main className="min-h-screen bg-gradient-warm">
      <section className="mx-auto max-w-2xl px-6 pt-12 pb-24">
        <Link
          to={`/${slug}`}
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-gentle"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to {person.firstName}'s page
        </Link>
        <div className="mt-6 text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-card/70 backdrop-blur px-4 py-1.5 text-xs uppercase tracking-[0.25em] text-primary shadow-soft">
            <Lock className="h-3 w-3" /> Family admin
          </div>
          <h1 className="mt-5 font-serif text-3xl sm:text-4xl text-foreground" data-testid="admin-heading">
            {person.fullName}'s page
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Whatever you post here appears on the page right away.
          </p>
        </div>

        <div className="mt-10 space-y-8">
          {/* Share card */}
          <div className="rounded-3xl bg-gradient-to-br from-card to-primary-soft/30 shadow-card p-6">
            <div className="flex items-center gap-2 mb-1">
              <Share2 className="h-4 w-4 text-primary" />
              <h2 className="font-serif text-xl text-foreground">Share {person.firstName}'s page</h2>
            </div>
            <p className="text-xs text-muted-foreground mb-4">
              A ready-to-send message with the link and passcode. Copy it and text it to family and friends.
            </p>
            <div
              className="rounded-2xl bg-secondary/60 px-4 py-3 text-sm text-foreground/85 whitespace-pre-line leading-relaxed"
              data-testid="share-text"
            >
              {shareText}
            </div>
            <div className="mt-3 flex justify-end">
              <button
                type="button"
                onClick={copyShareText}
                data-testid="share-copy-button"
                className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2 text-sm font-medium hover:bg-primary/90 transition-gentle"
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied ? "Copied" : "Copy message"}
              </button>
            </div>
          </div>

          {/* Post update */}
          <form onSubmit={postUpdate} className="rounded-3xl bg-card shadow-card p-6 space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <MessageCircle className="h-4 w-4 text-primary" />
              <h2 className="font-serif text-xl text-foreground">Post an update</h2>
            </div>
            <input
              className={inputCls}
              placeholder="Posted by (e.g. The Moore Family)"
              data-testid="update-author"
              value={update.author}
              onChange={(e) => setUpdate({ ...update, author: e.target.value })}
            />
            <input
              className={inputCls}
              placeholder="Title (optional — e.g. Good news today)"
              data-testid="update-title"
              value={update.title}
              onChange={(e) => setUpdate({ ...update, title: e.target.value })}
            />
            <textarea
              className={textareaCls}
              placeholder="Share how things are going…"
              data-testid="update-body"
              value={update.body}
              onChange={(e) => setUpdate({ ...update, body: e.target.value })}
            />
            <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
              <input
                type="checkbox"
                checked={update.fromCareTeam}
                data-testid="update-care-team"
                onChange={(e) => setUpdate({ ...update, fromCareTeam: e.target.checked })}
                className="h-4 w-4 rounded border-border accent-current text-primary"
              />
              This update came from the care team (doctors/nurses)
            </label>
            <div className="flex justify-end">
              <button type="submit" disabled={busy === "update"} data-testid="update-submit" className={submitCls}>
                {busy === "update" ? "Posting…" : "Post update"}
              </button>
            </div>
          </form>

          {/* Add need */}
          <form onSubmit={postNeed} className="rounded-3xl bg-card shadow-card p-6 space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <HandHeart className="h-4 w-4 text-primary" />
              <h2 className="font-serif text-xl text-foreground">Add a need</h2>
            </div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(needTypeLabels).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  data-testid={`need-type-${value}`}
                  onClick={() => setNeed({ ...need, type: value })}
                  className={`rounded-full px-4 py-1.5 text-xs font-medium transition-gentle ${
                    need.type === value
                      ? "bg-primary text-primary-foreground"
                      : "bg-primary-soft text-primary hover:bg-primary/20"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-1.5">Which board?</div>
              <div className="flex gap-2">
                {[
                  { value: "main", label: "Support his family" },
                  { value: "maya", label: "Back to School" },
                ].map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    data-testid={`need-category-${c.value}`}
                    onClick={() => setNeed({ ...need, category: c.value })}
                    className={`rounded-full px-4 py-1.5 text-xs font-medium transition-gentle ${
                      need.category === c.value
                        ? "bg-primary text-primary-foreground"
                        : "bg-secondary text-foreground hover:bg-secondary/70"
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
            <input
              className={inputCls}
              placeholder="What's needed (e.g. Dinner drop-off — Friday)"
              data-testid="need-title"
              value={need.title}
              onChange={(e) => setNeed({ ...need, title: e.target.value })}
            />
            <textarea
              className={textareaCls}
              placeholder="Any details helpers should know (optional)"
              data-testid="need-details"
              value={need.details}
              onChange={(e) => setNeed({ ...need, details: e.target.value })}
            />
            <div className="flex justify-end">
              <button type="submit" disabled={busy === "need"} data-testid="need-submit" className={submitCls}>
                {busy === "need" ? "Adding…" : "Add need"}
              </button>
            </div>
          </form>

          {/* Add appointment */}
          <form onSubmit={postMilestone} className="rounded-3xl bg-card shadow-card p-6 space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <CalendarDays className="h-4 w-4 text-primary" />
              <h2 className="font-serif text-xl text-foreground">Add an appointment</h2>
            </div>
            <input
              type="date"
              className={inputCls}
              data-testid="milestone-date"
              value={milestone.date}
              onChange={(e) => setMilestone({ ...milestone, date: e.target.value })}
            />
            <input
              className={inputCls}
              placeholder="Title (e.g. Follow-up with care team)"
              data-testid="milestone-title"
              value={milestone.title}
              onChange={(e) => setMilestone({ ...milestone, title: e.target.value })}
            />
            <input
              className={inputCls}
              placeholder="Note (e.g. 2:00pm — bring insurance card)"
              data-testid="milestone-note"
              value={milestone.note}
              onChange={(e) => setMilestone({ ...milestone, note: e.target.value })}
            />
            <div className="flex justify-end">
              <button type="submit" disabled={busy === "milestone"} data-testid="milestone-submit" className={submitCls}>
                {busy === "milestone" ? "Adding…" : "Add appointment"}
              </button>
            </div>
          </form>

          {/* Private questions inbox */}
          <div className="rounded-3xl bg-card shadow-card p-6">
            <div className="flex items-center gap-2 mb-1">
              <Inbox className="h-4 w-4 text-primary" />
              <h2 className="font-serif text-xl text-foreground">Questions from visitors</h2>
            </div>
            <p className="text-xs text-muted-foreground mb-4">
              Private to the family — visitors leave these through the "Reach out" form on the page.
            </p>
            {data.questions?.length ? (
              <div className="space-y-3" data-testid="questions-list">
                {data.questions.map((q) => (
                  <div key={q.id} className="rounded-2xl bg-secondary/50 p-4">
                    <div className="flex items-baseline justify-between gap-3 flex-wrap">
                      <div className="text-sm font-medium text-foreground">{q.name}</div>
                      <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{q.date}</span>
                    </div>
                    <p className="mt-1.5 text-sm text-foreground/80 leading-relaxed">{q.message}</p>
                    {(q.contact || q.contactMethod) && (
                      <p className="mt-2 text-xs text-primary">
                        Reach them{q.contactMethod !== "either" ? ` by ${q.contactMethod === "call" ? "call/text" : "email"}` : ""}
                        {q.contact ? `: ${q.contact}` : ""}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground" data-testid="questions-empty">
                No questions yet. They'll appear here when someone reaches out.
              </p>
            )}
          </div>

          {/* Full control panels */}
          <ContentEditor slug={slug} data={data} setData={setData} storedCode={storedCode} toast={toast} />
          <NeedsManager slug={slug} data={data} setData={setData} storedCode={storedCode} toast={toast} />
          <FeedManager slug={slug} data={data} setData={setData} storedCode={storedCode} toast={toast} />
        </div>

        <p className="mt-8 text-center text-xs text-muted-foreground">
          {data.updates.length} update{data.updates.length === 1 ? "" : "s"} · {data.needs.length} need
          {data.needs.length === 1 ? "" : "s"} · {data.milestones.length} appointment
          {data.milestones.length === 1 ? "" : "s"} on the page
        </p>
      </section>
    </main>
  );
};

export default AdminPage;
